import { useState, useRef } from "react";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaCheckCircle,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa";
import { message } from "antd";
import axios from "../../utils/axios";

const StepDot = ({ active, done, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 ${
        done
          ? "bg-green-500 border-green-500 text-white"
          : active
          ? "bg-blue-500 border-blue-500 text-white scale-110 shadow-lg shadow-blue-200"
          : "bg-white border-slate-200 text-slate-400"
      }`}
    >
      {done ? <FaCheckCircle className="w-4 h-4" /> : label}
    </div>
  </div>
);

const StepLine = ({ done }) => (
  <div className="flex-1 h-0.5 mx-1 mt-[-1rem] relative top-[1rem]">
    <div className={`h-full transition-all duration-500 ${done ? "bg-green-400" : "bg-slate-200"}`} />
  </div>
);

// ─────────────────────────────────────────────
// OTP Input – 6 boxes
// ─────────────────────────────────────────────
const OtpInput = ({ otp, setOtp }) => {
  const refs = useRef([]);

  const handleChange = (val, idx) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(""));
      refs.current[5]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {otp.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => (refs.current[idx] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e.target.value, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-xl text-slate-800 bg-white/70 
            focus:outline-none focus:border-blue-500 focus:bg-white focus:shadow-lg transition-all duration-200
            ${digit ? "border-blue-400 bg-white shadow-md" : "border-slate-200"}`}
        />
      ))}
    </div>
  );
};

const ForgotPasswordModal = ({ onClose }) => {
  const [step, setStep] = useState(1); // 1 = user info, 2 = OTP, 3 = new password
  const [fpUsername, setFpUsername] = useState("");
  const [fpEmail, setFpEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Step 1 – verify user
  const handleVerifyUser = async () => {
    if (!fpUsername.trim() || !fpEmail.trim()) {
      return message.warning("Please enter both username and email");
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/verify-user", {
        username: fpUsername.trim(),
        emailId: fpEmail.trim(),
      });
      // backend should send OTP here
      message.success(data?.msg || "OTP sent to your email");
      setStep(2);
    } catch (err) {
      message.error(err.response?.data?.msg || "User not found");
    } finally {
      setLoading(false);
    }
  };

  // Step 2 – verify OTP
  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      return message.warning("Please enter the 6-digit OTP");
    }

    setLoading(true);

    try {
      const { data } = await axios.post("/verify-otp", {
        emailId: fpEmail.trim(),   // ✅ FIXED
        otp: otpValue,
      });

      if (data?.success) {
        message.success(data?.message || "OTP verified successfully");
        setStep(3);
      } else {
        message.error(data?.message || "Invalid OTP");
      }

    } catch (err) {
      const status = err.response?.status;

      if (status === 410) {
        message.error("OTP expired. Please request a new one.");
        setOtp(["", "", "", "", "", ""]);
        setStep(1); // optional: go back to resend
      } else if (status === 401) {
        message.error("Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
      } else {
        message.error(err.response?.data?.message || "Something went wrong");
      }

    } finally {
      setLoading(false);
    }
  };

  // Step 3 – reset password
  const handleResetPassword = async () => {
    if (!newPassword.trim() || !confirmPassword.trim())
      return message.warning("Please fill all fields");
    if (newPassword !== confirmPassword)
      return message.error("Passwords do not match");
    if (newPassword.length < 6)
      return message.warning("Password must be at least 6 characters");

    setLoading(true);
    try {
      const { data } = await axios.post("/reset-password", {
        username: fpUsername.trim(),
        email: fpEmail.trim(),
        newPassword: newPassword,
      });
      message.success(data?.msg || "Password reset successfully");
      onClose();
    } catch (err) {
      message.error(err.response?.data?.msg || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = ["Verify Identity", "Enter OTP", "New Password"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden animate-modal-in">
        
        {/* Header gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <div className="p-8">
          {/* Title row */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 transition-colors text-slate-500"
                >
                  <FaArrowLeft className="w-3 h-3" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-bold text-slate-800">Forgot Password</h2>
                <p className="text-xs text-slate-400 mt-0.5">{stepTitles[step - 1]}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100 hover:bg-red-100 hover:text-red-500 transition-all text-slate-500"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center mb-8">
            <StepDot active={step === 1} done={step > 1} label="1" />
            <StepLine done={step > 1} />
            <StepDot active={step === 2} done={step > 2} label="2" />
            <StepLine done={step > 2} />
            <StepDot active={step === 3} done={false} label="3" />
          </div>

          {/* ── STEP 1: Username + Email ── */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 mb-5">
                Enter your username and registered email address to receive an OTP.
              </p>

              {/* Username */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <FaUser
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-all duration-300 ${
                    focusedField === "fp-user" ? "text-blue-500" : "text-slate-400"
                  }`}
                />
                <input
                  type="text"
                  value={fpUsername}
                  onChange={(e) => setFpUsername(e.target.value)}
                  onFocus={() => setFocusedField("fp-user")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Username"
                  className="w-full h-12 pl-11 pr-4 bg-white/70 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:bg-white focus:shadow-lg transition-all duration-300 relative z-10"
                />
                <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300 ${focusedField === "fp-user" ? "w-full" : "w-0"}`} />
              </div>

              {/* Email */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <FaEnvelope
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-all duration-300 ${
                    focusedField === "fp-email" ? "text-indigo-500" : "text-slate-400"
                  }`}
                />
                <input
                  type="email"
                  value={fpEmail}
                  onChange={(e) => setFpEmail(e.target.value)}
                  onFocus={() => setFocusedField("fp-email")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Email address"
                  className="w-full h-12 pl-11 pr-4 bg-white/70 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:bg-white focus:shadow-lg transition-all duration-300 relative z-10"
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyUser()}
                />
                <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 ${focusedField === "fp-email" ? "w-full" : "w-0"}`} />
              </div>

              <button
                onClick={handleVerifyUser}
                disabled={loading}
                className="w-full h-12 mt-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Verifying...</>
                ) : (
                  "Send OTP"
                )}
              </button>
            </div>
          )}

          {/* ── STEP 2: OTP ── */}
          {step === 2 && (
            <div className="space-y-6">
              <p className="text-sm text-slate-500 text-center">
                A 6-digit OTP has been sent to <span className="font-semibold text-slate-700">{fpEmail}</span>. Please enter it below.
              </p>

              <OtpInput otp={otp} setOtp={setOtp} />

              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Verifying OTP...</>
                ) : (
                  "Verify OTP"
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => { setOtp(["","","","","",""]); handleVerifyUser(); }}
                  className="text-xs text-slate-400 hover:text-blue-500 transition-colors underline underline-offset-2"
                >
                  Didn't receive OTP? Resend
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: New Password ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 mb-2">
                Set a new password for your account.
              </p>

              {/* New Password */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <FaLock
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-all duration-300 ${
                    focusedField === "fp-new" ? "text-blue-500" : "text-slate-400"
                  }`}
                />
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onFocus={() => setFocusedField("fp-new")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="New password"
                  className="w-full h-12 pl-11 pr-11 bg-white/70 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:bg-white focus:shadow-lg transition-all duration-300 relative z-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors z-20"
                >
                  {showNew ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
                <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300 ${focusedField === "fp-new" ? "w-full" : "w-0"}`} />
              </div>

              {/* Confirm Password */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <FaLock
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-all duration-300 ${
                    focusedField === "fp-confirm" ? "text-indigo-500" : "text-slate-400"
                  }`}
                />
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setFocusedField("fp-confirm")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Confirm new password"
                  className="w-full h-12 pl-11 pr-11 bg-white/70 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:bg-white focus:shadow-lg transition-all duration-300 relative z-10"
                  onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors z-20"
                >
                  {showConfirm ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
                <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 ${focusedField === "fp-confirm" ? "w-full" : "w-0"}`} />
              </div>

              {/* Password match indicator */}
              {confirmPassword && (
                <p
                  className={`text-xs font-medium flex items-center gap-1.5 ${
                    newPassword === confirmPassword ? "text-green-500" : "text-red-400"
                  }`}
                >
                  {newPassword === confirmPassword ? (
                    <><FaCheckCircle /> Passwords match</>
                  ) : (
                    <><FaTimes /> Passwords do not match</>
                  )}
                </p>
              )}

              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full h-12 mt-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Resetting...</>
                ) : (
                  <><FaCheckCircle className="w-4 h-4" /> Reset Password</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Inline CSS for modal animation */}
      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in { animation: modal-in 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
      `}</style>
    </div>
  );
};

export default ForgotPasswordModal